import { GridColDef } from "@mui/x-data-grid-pro";
import { useMutation } from "@tanstack/react-query";
import {
  ArrowRightIcon,
  Box,
  Card,
  CircularProgress,
  Divider,
  GKButton,
  GKDataGrid,
  GKSearchSelect,
  Grid2,
  Typography,
} from "components";
import { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import axiosInstance from "utils/axiosInstance";
import { extraColumnsField } from "utils/helpers";
import PairwiseChat from "./PairwiseChat";

interface Props {
  mfHoldingsData: any;
  resetOverlapKey: any;
}

const PairwiseSchemeOverlap = (props: Props) => {
  const { mfHoldingsData } = props;

  const [selectedFundAName, setSelectedFundAName] = useState("");

  const [selectedFundBName, setSelectedFundBName] = useState("");

  const [overlapPercentage, setOverlapPercentage] = useState(0);

  const [commonStocks, setCommonStocks] = useState(0);

  const [totalStocks, setTotalStocks] = useState(0);

  const [overlapRows, setOverlapRows] = useState([]);

  const form = useForm({
    defaultValues: {
      fundA: null,
      fundB: null,
    },
  });

  const fundAValue = form.watch("fundA");
  const fundBValue = form.watch("fundB");

  const columns: GridColDef[] = [
    {
      ...extraColumnsField("Stock Name", "stockName", 80, 1),
    },
    {
      ...extraColumnsField("Fund A", "fundA", 10, 1),
    },
    {
      ...extraColumnsField("Fund B", "fundB", 10, 1),
    },
  ];

  const { mutate: checkOverlap, isLoading } = useMutation({
    mutationKey: ["CHECK_OVER_LAP"],
    mutationFn: (data: any) => axiosInstance.post(`/holdings/mf_stocks/`, data),
    onSuccess: (res) => {
      setOverlapPercentage(res?.data?.data?.overlapPercentage || 0);
      setTotalStocks(res?.data?.data?.totalStocks || 0);
      setCommonStocks(res?.data?.data?.commonStocks || 0);
      const rows = (res?.data?.data?.data || []).map(
        (row: any, index: number) => ({
          id: index + 1,
          ...row,
        }),
      );
      setOverlapRows(rows);
    },
  });

  useEffect(() => {
    setSelectedFundAName("");
    setSelectedFundBName("");
    setOverlapPercentage(0);
    setCommonStocks(0);
    setTotalStocks(0);

    form.reset({
      fundA: null,
      fundB: null,
    });
  }, [props.resetOverlapKey]);

  return (
    <Card sx={{ p: 0, mt: 2 }}>
      <Grid2
        sx={{
          p: 1.5,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          borderBottom: "1px solid",
          borderColor: "#E3E8EF",
        }}
      >
        <Typography fontSize={14} fontWeight={700} color="#333">
          Pairwise Scheme Overlap
        </Typography>
      </Grid2>

      <Grid2 container size={12}>
        <Grid2 size={4}>
          <Grid2 sx={{ py: 3.5, px: 2 }}>
            <Typography
              fontSize={12}
              fontWeight={400}
              color="var(--neutral-700)"
            >
              Choose 2 mutual fund schemes for comparison.
            </Typography>
          </Grid2>
          <Grid2
            size={12}
            display="flex"
            alignItems="center"
            gap={1}
            sx={{ px: 2 }}
          >
            <Typography fontSize={13} fontWeight={500} color="var(--grey-900)">
              Fund A
            </Typography>

            <Box
              sx={{
                width: 12,
                height: 12,
                borderRadius: "50%",
                backgroundColor: "var(--primary-700)",
              }}
            />
          </Grid2>

          <Grid2 size={12} sx={{ px: 2, mt: 1 }}>
            <Controller
              name="fundA"
              control={form.control}
              render={({ field }) => (
                <GKSearchSelect
                  {...field}
                  inputLabel="Fund A"
                  options={
                    mfHoldingsData?.map((item: any) => ({
                      name: item?.asset?.fullname || item?.asset?.name,
                      value: item.asset.gcode,
                    })) || []
                  }
                  getOptionLabel={(option) => option.fullname || option.name}
                  onChange={(_, value) => {
                    field.onChange(value);
                  }}
                  value={field.value || null}
                />
              )}
            />
          </Grid2>
          <Grid2
            size={12}
            display="flex"
            alignItems="center"
            gap={1}
            sx={{ px: 2, mt: 2 }}
          >
            <Typography fontSize={13} fontWeight={500} color="var(--grey-900)">
              Fund B
            </Typography>

            <Box
              sx={{
                width: 12,
                height: 12,
                borderRadius: "50%",
                backgroundColor: "#63A387",
              }}
            />
          </Grid2>

          <Grid2 size={12} sx={{ px: 2, mt: 1 }}>
            <Controller
              name="fundB"
              control={form.control}
              render={({ field }) => (
                <GKSearchSelect
                  inputLabel="Fund B"
                  options={
                    mfHoldingsData?.map((item: any) => ({
                      name: item?.asset?.fullname || item?.asset?.name,
                      value: item.asset.gcode,
                    })) || []
                  }
                  getOptionLabel={(option) => option.fullname || option.name}
                  onChange={(_, value) => {
                    field.onChange(value);
                  }}
                  value={field.value || null}
                />
              )}
            />
          </Grid2>

          <Grid2 size={12} sx={{ p: 2, mt: 3 }}>
            <GKButton
              fullWidth
              variant="contained"
              endIcon={<ArrowRightIcon />}
              disabled={!fundAValue || !fundBValue}
              onClick={() => {
                const fundA = form.getValues("fundA");
                const fundB = form.getValues("fundB");

                setSelectedFundAName(fundA.name);
                setSelectedFundBName(fundB.name);

                checkOverlap({
                  fundA: fundA.value,
                  fundB: fundB.value,
                });
              }}
            >
              Check Overlap
            </GKButton>
          </Grid2>
        </Grid2>
        <Divider orientation="vertical" flexItem />
        <Grid2
          size={3.9}
          p={2.5}
          display="flex"
          flexDirection="column"
          alignItems="center"
        >
          <PairwiseChat
            fundAName={selectedFundAName}
            fundBName={selectedFundBName}
            overlapPercentage={overlapPercentage}
          />

          <Grid2
            sx={{
              p: 2,
              mt: 2,
              alignSelf: "flex-start",
              display: "flex",
              flexDirection: "column",
              gap: 1,
            }}
          >
            <Box display="flex" alignItems="center" gap={0.8}>
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  borderRadius: "50%",
                  backgroundColor: "var(--primary-700)",
                }}
              />

              {isLoading ? (
                <CircularProgress size={12} />
              ) : (
                <Typography
                  fontSize={12}
                  fontWeight={400}
                  color="var(--grey-500)"
                >
                  {selectedFundAName || ""}
                </Typography>
              )}
            </Box>

            <Box display="flex" alignItems="center" gap={0.8}>
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  borderRadius: "50%",
                  backgroundColor: "#63A387",
                }}
              />

              {isLoading ? (
                <CircularProgress size={12} />
              ) : (
                <Typography
                  fontSize={12}
                  fontWeight={400}
                  color="var(--grey-500)"
                >
                  {selectedFundBName || ""}
                </Typography>
              )}
            </Box>
          </Grid2>
          <Grid2
            p={1.5}
            size={12}
            display="flex"
            flexDirection="column"
            bgcolor={"var(--grey-bg-100)"}
            borderRadius={2}
            gap={1.5}
            mb={2.5}
          >
            <Grid2
              size={12}
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Typography
                fontSize={14}
                fontWeight={600}
                color="var(--grey-900)"
              >
                {/* {typeof overlapPercentage === "number"
                  ? overlapPercentage.toFixed(2)
                  : "0.00"} */}
                {overlapPercentage.toFixed(2)}% % Overlap
              </Typography>
              <Typography>Moderate Overlap</Typography>
            </Grid2>

            <Grid2>
              <Typography
                fontSize={12}
                fontWeight={400}
                color="var(--black-900)"
              >
                {commonStocks}/{totalStocks} Stocks Overlapping in Mutual Funds
                Schemes
              </Typography>
            </Grid2>
          </Grid2>
        </Grid2>

        <Divider orientation="vertical" flexItem />
        <Grid2 size={4}>
          <GKDataGrid
            loading={false}
            columns={columns}
            rows={overlapRows}
            name=""
            showSearch={false}
            showHeaderGrid={false}
            density="compact"
            maxHeight={"calc(100vh - 230px)"}
          />
        </Grid2>
      </Grid2>
    </Card>
  );
};

export default PairwiseSchemeOverlap;
